import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API_ROUTES, type ListResponse, type Tournament, type TournamentStatus } from '@brochure/shared'
import { apiFetch } from '@/lib/api'
import { Sticker } from '@/components/ui/Sticker'
import { LocalizedLink } from '@/components/LocalizedLink'
import { ArrowRight } from 'lucide-react'
import { formatTournamentDate } from '@/lib/zambepong-format'
import { cn } from '@/lib/utils'

// Punto di stato: oro = iscrizioni aperte, bordeaux = in corso, inchiostro tenue = chiuso/concluso.
const STATUS_DOTS: Record<TournamentStatus, string> = {
  registration: 'bg-sun',
  active: 'bg-coral',
  closed: 'bg-ink-soft/50',
  completed: 'bg-ink-soft/50',
}

// Ordine di visualizzazione: iscrizioni aperte prima, poi in corso, poi il resto (chiuso/concluso).
const STATUS_RANK: Record<TournamentStatus, number> = { registration: 0, active: 1, closed: 2, completed: 3 }

function sortTournaments(tournaments: readonly Tournament[]): Tournament[] {
  return [...tournaments].sort((a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status])
}

/** Lista pubblica dei tornei ZambePong, ordinata per stato: si iscrive prima chi guarda per primo. */
export function TournamentsPage() {
  const { t, i18n } = useTranslation()
  const [tournaments, setTournaments] = useState<Tournament[] | null>(null)
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    apiFetch<ListResponse<Tournament>>(`${API_ROUTES.tournaments}?sort=date&order=desc&limit=100`, { signal: controller.signal })
      .then((response) => setTournaments(sortTournaments(response.items)))
      .catch(() => { if (!controller.signal.aborted) setHasError(true) })
    return () => controller.abort()
  }, [])

  return (
    <main id="main" className="mx-auto max-w-7xl px-4 pt-24 pb-section sm:px-6 md:pt-28 lg:px-8">
      <div className="flex items-end justify-between gap-6">
        <div>
          <p className="inscription text-xl text-coral-ink">{t('zambePong.eyebrow')}</p>
          <h1 className="hero-title mt-1 text-hero text-ink lg:text-[6rem]/[0.95]">{t('zambePong.title')}</h1>
          <p className="mt-5 max-w-[56ch] font-body text-lg leading-relaxed text-ink-soft">{t('zambePong.intro')}</p>
        </div>
        <Sticker name="trophy" float rotate={6} priority className="hidden w-44 shrink-0 sm:block lg:w-56" />
      </div>

      {hasError && <p role="alert" className="mt-10 max-w-prose text-body text-ink-soft">{t('zambePong.error.list')}</p>}
      {tournaments?.length === 0 && <p className="mt-10 max-w-prose text-body text-ink-soft">{t('zambePong.empty')}</p>}

      <ul className="mt-8 grid gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
        {tournaments?.map((tournament) => (
          <li key={tournament.id}>
            {/* Cartoncino d'avorio: stato, nome del torneo in Cormorant, data e squadre; al passaggio si alza e si accende il filetto d'oro. */}
            <LocalizedLink
              to="zambePongTournament"
              params={{ id: tournament.id }}
              className="group flex h-full flex-col rounded-2xl border border-ink/10 bg-paper p-1.5 text-ink shadow-card transition-[transform,box-shadow,border-color] duration-300 ease-out-expo hover:-translate-y-1 hover:border-sea/40 hover:shadow-lift hover:-rotate-[0.5deg] active:translate-y-0 active:duration-75"
            >
              <span className="flex h-full flex-col border border-transparent px-5 pt-5 pb-5 transition-colors duration-300 group-hover:border-transparent">
                <span className="flex items-center gap-2 font-body text-sm font-medium text-ink-soft">
                  <span aria-hidden="true" className={cn('size-2 rounded-full', STATUS_DOTS[tournament.status], tournament.status === 'active' && 'motion-safe:animate-pulse')} />
                  {t(`zambePong.status.${tournament.status}`)}
                </span>
                <span className="mt-3 block font-display text-3xl font-bold leading-tight">{tournament.name}</span>
                <span className="mt-auto flex items-center justify-between gap-3 pt-5 font-body text-sm text-ink-soft">
                  <span>
                    {formatTournamentDate(tournament.date, i18n.language)}
                    <span aria-hidden="true" className="mx-2 inline-block size-1.5 rounded-full bg-sea align-middle" />
                    {tournament.teamCount > 0
                      ? t('zambePong.counts.teams', { count: tournament.teamCount })
                      : t('zambePong.counts.participants', { count: tournament.participantCount })}
                  </span>
                  <ArrowRight aria-hidden="true" className="size-4 shrink-0 transition-transform duration-300 ease-out-expo group-hover:translate-x-1" />
                </span>
              </span>
            </LocalizedLink>
          </li>
        ))}
      </ul>
    </main>
  )
}
